import { Fragment, ReactNode, useMemo, useState } from 'react';

import { cx } from '@libs/classnames';
import { useMediaQuery } from '@providers/MediaQueryProvider';
import { WideScreenNavigation } from '@ui/components/WideScreenNavigation/WideScreenNavigation';

import {
  KnockoutResultsMatch,
  KnockoutResultsRound,
  KnockoutRoundType,
} from '@features/shared/api/typings';
import { BracketsMatch } from '@features/tournament/ui/components/BracketsMatch';
import { KnockoutRoundName } from '@features/tournament/ui/components/KnockoutRoundName';

import styles from './EliminationBrackets.module.css';

export type EliminationBracketsProps = {
  className?: string;
  rounds: KnockoutResultsRound[];
  onGameClick?: (gameId: string) => void;
  renderShieldQuickPeek?: (userId: string) => ReactNode;
};

const wideNavigationClassName = 'roundWideNavigation';

export function EliminationBrackets({
  className,
  rounds,
  onGameClick,
  renderShieldQuickPeek,
}: EliminationBracketsProps) {
  const [currentPage, setCurrentPage] = useState(0);
  const filtered = useMemo(
    () => rounds.filter((r) => r.roundType === 'WinnerBranch' || r.roundType === 'Final'),
    [rounds],
  );

  return (
    <div className={cx(styles.brackets, className)}>
      <WideScreenNavigation
        pageClassName={wideNavigationClassName}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
      >
        <div className={styles.bracket}>
          {filtered.map((r, i) => {
            const roundIndex = ++i; // omg, why ++?
            const roundPosition = i - currentPage;
            const isHidden = roundPosition - 1 < 0;
            const isLast = i === filtered.length;

            return (
              <EliminationRound
                key={r.roundName}
                roundIndex={roundIndex}
                roundName={r.roundName}
                roundPosition={i - currentPage}
                isHidden={isHidden}
                isLast={isLast}
                roundType={r.roundType}
                matches={r.matches}
                onGameClick={onGameClick}
                renderShieldQuickPeek={renderShieldQuickPeek}
              />
            );
          })}
        </div>
      </WideScreenNavigation>
    </div>
  );
}

type EliminationRoundProps = {
  roundIndex: number;
  roundName: string;
  roundPosition?: number;
  isHidden: boolean;
  isLast: boolean;
  roundType: KnockoutRoundType;
  matches: KnockoutResultsMatch[];
  onGameClick?: (gameId: string) => void;
  renderShieldQuickPeek?: (userId: string) => ReactNode;
};
function EliminationRound({
  roundIndex,
  roundName,
  roundType,
  matches,
  roundPosition = 1,
  isHidden,
  isLast,
  onGameClick,
  renderShieldQuickPeek,
}: EliminationRoundProps) {
  const isMobile = useMediaQuery() === 'mobile';
  const isFirst = roundPosition === 1;
  const isOnly = isFirst && isLast;

  return (
    <>
      {matches.map((m, i) => {
        const gridColumn = roundIndex;
        const gridRow = Math.pow(2, roundIndex) * i + Math.pow(2, roundIndex - 1);

        const placeholderRow = (() => {
          const needsPlaceholder = matches.length > 1 && i < matches.length - 1;
          if (!needsPlaceholder) return null;

          // placeholder в финале должен быть на месте 2 матча полуфинала
          if (m.knockoutRoundType === 'Final') {
            return Math.pow(2, roundIndex - 1) * 1 + Math.pow(2, roundIndex - 2);
          }
          // для всех остальных матчей — ровно между
          const nextGridRow = Math.pow(2, roundIndex) * (i + 1) + Math.pow(2, roundIndex - 1);
          return (gridRow + nextGridRow) / 2;
        })();

        return (
          <Fragment
            key={
              'match' +
              m.player1?.userId +
              m.player1?.lastName +
              m.player2?.userId +
              m.player2?.lastName
            }
          >
            <div
              className={cx(styles.match, {
                [wideNavigationClassName]: i === 0,
                [styles.hidden]: isHidden,
              })}
              style={{
                gridColumn,
                gridRow: gridRow,
              }}
            >
              <BracketsMatch
                roundName={
                  <span className={styles.roundName}>
                    <KnockoutRoundName
                      roundName={roundName}
                      roundType={m.knockoutRoundType || roundType}
                      preset={isMobile ? 'full' : 'short'}
                    />
                  </span>
                }
                player1={m.player1 || undefined}
                player2={m.player2 || undefined}
                isFinal={m.knockoutRoundType === 'Final'}
                isThirdPlace={m.knockoutRoundType === 'ThirdPlace'}
                onGameClick={onGameClick}
                renderShieldQuickPeek={renderShieldQuickPeek}
              />
            </div>
            {placeholderRow && (
              <div
                className={cx(styles.placeholder, {
                  [styles.is_high]: !isOnly && !isHidden,
                  [styles.is_hidden]: isHidden,
                })}
                style={{
                  gridColumn,
                  gridRow: placeholderRow,
                }}
              />
            )}
          </Fragment>
        );
      })}
    </>
  );
}
